import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ShipmentNotificationParams } from '@/interfaces/shipmentNotification'
import APIServices from '@/api/APIServices'

interface CreateShipmentNotificationResponse {
  success: boolean
  data?: any
  message?: string
}

const createShipmentNotification = async (data: ShipmentNotificationParams): Promise<CreateShipmentNotificationResponse> => {
  try {
    // Validate required fields
    if (!data.shouldArrivedAt) {
      throw new Error('shouldArrivedAt is required')
    }
    if (!data.providerCompanyId) {
      throw new Error('providerCompanyId is required')
    }
    if (!data.managerCompanyId) {
      throw new Error('managerCompanyId is required')
    }
    
    // Ensure data types are correct and create minimal request
    const requestData: any = {
      shouldArrivedAt: data.shouldArrivedAt,
      providerCompanyId: Number(data.providerCompanyId),
      managerCompanyId: Number(data.managerCompanyId),
    }
    
    // Only add optional fields if they exist
    if (data.name && data.name.trim()) {
      requestData.name = data.name.trim()
    }
    
    if (data.observations && data.observations.trim()) {
      requestData.observations = data.observations.trim()
    }
    
    const response = await APIServices.post('/cargo/shippingnotification', requestData)
    
    return {
      success: true,
      data: response.data,
      message: 'Notificación de embarque creada exitosamente'
    }
  } catch (error: any) {
    console.error('Error creating shipment notification:', error)
    
    let errorMessage = 'Error al crear la notificación de embarque'
    
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        errorMessage = error.response.data
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error
      } else if (error.response.data.errors) {
        errorMessage = JSON.stringify(error.response.data.errors)
      }
    } else if (error.message) {
      errorMessage = error.message
    }
    
    return {
      success: false,
      message: errorMessage
    }
  }
}

export const useCreateShipmentNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createShipmentNotification,
    onSuccess: (result) => {
      if (result.success) {
        // Invalidar la query de la lista para refrescar los datos
        queryClient.invalidateQueries({ 
          queryKey: ['shipment-notifications'] 
        })
      }
    },
    onError: (error) => {
      console.error('Mutation error:', error)
    }
  })
}
